// import { NewAlert } from "@/types";
import { ROUTE_LOGIN_OUT } from "@/constants/routes.constants";
import { fetchAgentModels } from "@/models/api/agent.model";
import { fetchConversations } from "@/models/api/conversation.model";
import {
  locForGetAgentModels,
  locForSetAgentModels,
} from "@/models/localForage/agents.model";
import {
  locForGetConversations,
  locForSetConversations,
} from "@/models/localForage/conversations.model";
import { AppContext } from "@/services/AppContext/appContext";
import { Button } from "@/shadcn/components/ui/button";
import {
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/shadcn/components/ui/card";
import { AgentModel, Conversation } from "@/types";
import { genericFailureToast, genericSuccessToast } from "@/utils/utils.util";
import isEqual from "fast-deep-equal";
import React, {
  ReactNode,
  useCallback,
  useLayoutEffect,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

interface AppProviderProps {
  children: ReactNode;
}
const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [agentsModels, setAgentsModels] = useState<AgentModel[] | undefined>(
    undefined
  );
  const [conversations, setConversations] = useState<
    Conversation[] | undefined
  >(undefined);
  const [selectedAgentModel, setSelectedAgentModel] =
    useState<AgentModel | null>(null);
  const [noAccess, setNoAccess] = useState(false);

  const initialGetAllAgentModels = useCallback(
    async (force?: boolean) => {
      console.log("Fetching agents");
      const lsAllAgentModels = await locForGetAgentModels();
      if (force || !lsAllAgentModels || lsAllAgentModels.length === 0) {
        console.log("Fetching agents from API");
        const response = await fetchAgentModels({});
        if (response.rStatus === "error") {
          console.error("Error fetching agents:", response.rMessage);
          setNoAccess(true);
          // genericFailureToast(response.rMessage);
          return;
        }
        setNoAccess(false);
        await locForSetAgentModels(response.rData);

        if (!isEqual(response.rData, agentsModels)) {
          console.log("different agents, setting agents");
          setAgentsModels(response.rData);
        } else {
          console.log("same agents, not setting agents");
        }

        if (force) {
          genericSuccessToast(t("Agents fetched successfully"));
        }
        return;
      }

      if (!isEqual(lsAllAgentModels, agentsModels)) {
        console.log("Agents are different, setting agents");
        setAgentsModels(lsAllAgentModels);
      } else {
        console.log("same agents, not setting agents");
      }
    },
    [t, agentsModels]
  );

  const initialGetAllConversations = useCallback(
    async (force?: boolean) => {
      const lsAllConversations = await locForGetConversations();
      if (force || !lsAllConversations || lsAllConversations.length === 0) {
        const response = await fetchConversations({});
        if (response.rStatus === "error") {
          console.error("Error fetching conversations:", response.rMessage);
          genericFailureToast(response.rMessage);
          return;
        }

        // sort data by last_date, newest first
        // convert last_date to Date object for sorting
        response.rData.sort(
          (a, b) =>
            new Date(b.last_date).getTime() - new Date(a.last_date).getTime()
        );

        await locForSetConversations(response.rData);

        if (!isEqual(response.rData, lsAllConversations)) {
          console.log("different conversations, setting conversations");

          setConversations(response.rData);
        } else {
          console.log("same conversations, not setting conversations");
        }

        if (force) {
          genericSuccessToast(t("Conversations fetched successfully"));
        }

        return;
      }

      if (!isEqual(lsAllConversations, conversations)) {
        console.log("Conversations are different, setting conversations");
        setConversations(lsAllConversations);
      } else {
        console.log("same conversations, not setting conversations");
      }
    },
    [t, conversations]
  );

  useLayoutEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    async function fetchData() {
      try {
        await Promise.all([
          initialGetAllAgentModels(),
          initialGetAllConversations(),
        ]);
      } catch (error) {
        if (signal.aborted) {
          console.log("Fetch aborted");
        } else {
          console.error("Fetch error:", error);
        }
      }
    }

    fetchData();

    return () => {
      controller.abort();
    };
  }, [initialGetAllAgentModels, initialGetAllConversations]);

  function addConversation(conversation: Conversation) {
    if (!conversation) {
      console.error("Conversation is required");
      return;
    }
    setConversations((prev) => [conversation, ...(prev || [])]); // Ensure 'prev' is defined before spreading
    locForSetConversations([conversation, ...(conversations || [])]); // Ensure 'conversations' is defined before spreading
  }

  function updateConversation(conversation: Conversation) {
    if (!conversation) {
      console.error("Conversation is required");
      return;
    }
    setConversations((prev) => {
      if (!prev) return [conversation]; // If prev is undefined, return a new array with the conversation
      const index = prev.findIndex((c) => c.id === conversation.id);
      if (index !== -1) {
        const updatedConversations = [...prev];
        updatedConversations[index] = conversation;
        locForSetConversations(updatedConversations);
        return updatedConversations;
      }
      return [...prev, conversation]; // If not found, add it to the list
    });
  }

  // TODO: revisit
  if (noAccess) {
    return (
      <div className="bg-opacity-50 bg-card fixed inset-0 z-50 flex items-center justify-center">
        <div className="fixed top-1/2 left-1/2 flex h-full w-full -translate-x-1/2 -translate-y-1/2 items-center justify-center p-5">
          <Card className="flex h-full w-full justify-between border-0 shadow-none">
            <CardHeader>
              <CardTitle className="text-center text-2xl">
                {t("No Agents Found")}
              </CardTitle>
              <CardDescription className="text-center">
                <div className="grow">Please contact BeOne Support</div>
              </CardDescription>
            </CardHeader>
            <CardFooter className="flex flex-col items-center justify-center text-sm">
              <Button
                variant={"link"}
                className="p-1 font-bold text-blue-400"
                onClick={() => {
                  setNoAccess(false);
                  nav(ROUTE_LOGIN_OUT);
                }}
              >
                {t("Logout")}
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    );
  } else if (agentsModels === undefined || conversations === undefined) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="border-primary flex h-10 w-10 animate-spin items-center justify-center rounded-full border-4 border-t-4 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <AppContext.Provider
      value={{
        agentsModels,
        refreshAllAgentModels: () => initialGetAllAgentModels(true),
        conversations,
        refreshAllConversations: () => initialGetAllConversations(true),
        selectedAgentModel,
        setSelectedAgentModel,
        addConversation,
        updateConversation,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export default AppProvider;
